import type { KeyboardEvent } from 'react';
import { Badge } from '../../components/Badge';
import { DiscountPriceDisplay } from '../../components/PriceDisplay';
import {
  CardBase,
  Content,
  ContentBadge,
  Image,
  ImageBadge,
  ImageWrapper,
  Meta,
  MetaRow,
  Placeholder,
  Price,
  SoldOutBadge,
  SpotsLeft,
  Time,
  Title,
  TitleRow,
} from './SpaClassCard.styles';
import type { SpaClassCardProps } from './SpaClassCard.types';

const defaultFormatSpotsLeft = (spots: number) => `${spots} ${spots === 1 ? 'spot' : 'spots'} left`;

function buildMeta(duration: string | undefined, instructor: string | undefined): string {
  const parts: string[] = [];
  if (duration) parts.push(duration);
  if (instructor) parts.push(instructor);
  return parts.join(' · ');
}

export function SpaClassCard({
  id,
  name,
  time,
  imageUrl,
  hideImage,
  price,
  discount,
  duration,
  instructor,
  spotsLeft,
  lowAvailabilityThreshold = 2,
  unavailable,
  promotionName,
  noImageLabel = 'No image',
  soldOutLabel = 'Sold out',
  formatSpotsLeft = defaultFormatSpotsLeft,
  onSelect,
}: SpaClassCardProps) {
  const meta = buildMeta(duration, instructor);
  const clickable = Boolean(onSelect);

  const trigger = () => {
    if (unavailable) return;
    onSelect(id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      trigger();
    }
  };

  const showSpotsLeft = !unavailable && typeof spotsLeft === 'number' && spotsLeft > 0;
  const isLow = typeof spotsLeft === 'number' && spotsLeft <= lowAvailabilityThreshold;

  return (
    <CardBase
      data-testid="spa-class-card"
      $unavailable={unavailable}
      $clickable={clickable}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? (unavailable ? -1 : 0) : undefined}
      aria-disabled={unavailable || undefined}
      aria-label={`${name} ${time}`}
      onClick={clickable ? trigger : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
    >
      {!hideImage && (
        <ImageWrapper>
          {imageUrl ? (
            <Image src={imageUrl} alt={name} />
          ) : (
            <Placeholder>{noImageLabel}</Placeholder>
          )}
          {promotionName && <ImageBadge>{promotionName}</ImageBadge>}
        </ImageWrapper>
      )}

      <Content>
        {hideImage && promotionName && <ContentBadge>{promotionName}</ContentBadge>}
        <TitleRow>
          <Title>{name}</Title>
          {!unavailable && price && (
            <Price>
              <DiscountPriceDisplay price={price} discount={discount} size="small" />
            </Price>
          )}
        </TitleRow>

        <Time>
          <Badge tone="neutral">{time}</Badge>
        </Time>

        <MetaRow>
          {meta ? <Meta>{meta}</Meta> : <span />}
          {unavailable ? (
            <SoldOutBadge tone="neutral">{soldOutLabel}</SoldOutBadge>
          ) : showSpotsLeft ? (
            <SpotsLeft $low={isLow}>{formatSpotsLeft(spotsLeft as number)}</SpotsLeft>
          ) : null}
        </MetaRow>
      </Content>
    </CardBase>
  );
}
